import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouteMatch } from 'react-router-dom';

import { AccountModuleTypes, ModuleTypes } from 'dashboard/apps/DashboardLayoutEditor/constants';
import { ModuleTypeInfo } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import {
  DataDisplayPanel,
  GraphV2Panel,
  MarkdownPanel,
  ResourceInquiryV2Panel,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels';
import AccountInfoPanel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/AccountInfoPanel';
import ResourceReportV2Panel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceReportV2Panel';
import ResourceVerificationV2Panel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceVerificationV2Panel';
import LayoutEditor from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import { FormEditAccountLayoutTab } from 'dashboard/forms/FormEditAccountLayoutTab';
import { AccountType } from 'dashboard/models/account-type';
import { AccountModule, AccountTypeGridLayout } from 'dashboard/models/account-type-grid-layouts';
import { Environment } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Configuration, Tab } from 'dashboard/models/grid-layout';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import AccountModuleBasePreview from 'dashboard/modules/AccountModuleBase/AccountModuleBasePreview';
import { TOP_BUTTON_CONTAINER_PORTAL_ID } from 'dashboard/views/DashboardAccountTypesShowView/DashboardAccountTypesShowView';
import {
  generateDefaultAccountModuleConfiguration,
  generateDefaultAccountTypeGridLayoutConfiguration,
} from 'dashboard/views/DashboardAccountTypesShowView/helpers';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { FieldSchema } from 'lib/fields';
import { JsonSchemaObject } from 'lib/json-schema';

const accountModuleTypes: Record<string, ModuleTypeInfo<AccountModuleTypes>> = {
  ACCOUNT_INFO: {
    type: ModuleTypes.AccountInfo,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  DATA_DISPLAY: {
    type: ModuleTypes.DataDisplay,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  RELATIONS: {
    type: ModuleTypes.Relations,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  RELATED_CASES: {
    type: ModuleTypes.RelatedCases,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  MARKDOWN: {
    type: ModuleTypes.Markdown,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  TAGS: {
    type: ModuleTypes.Tags,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INQUIRY: {
    type: ModuleTypes.Inquiryv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  REPORT: {
    type: ModuleTypes.Reportv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  VERIFICATION: {
    type: ModuleTypes.Verificationv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  EVENT_TIMELINE: {
    type: ModuleTypes.EventTimeline,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  GRAPH: {
    type: ModuleTypes.Graphv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  LIST_MATCHES: {
    type: ModuleTypes.ListMatches,
    featureFlag: 'dashboard:decouple-list-matches-from-inquiries',
    productFeatures: null,
    internalUsersOnly: false,
  },
};

export interface AccountLayoutEditorProps {
  accountType: AccountType;
  organization: Organization;
  currentEnvironment: Environment;
  dataSchema: JsonSchemaObject;
  graph: Graph | undefined;
  graphQueryTemplates: GraphQueryTemplate[];
  gridLayouts: AccountTypeGridLayout[];
  fieldSchema: FieldSchema[];
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
  onAccountTypeGridLayoutCreate: (configuration: Configuration<AccountModule>) => void;
  onAccountTypeGridLayoutSave: (gridLayout: AccountTypeGridLayout) => void;
}

export const AccountLayoutEditor = (props: AccountLayoutEditorProps) => {
  const {
    params: { accountTypeId },
  } = useRouteMatch<{ accountTypeId: string }>();
  const {
    accountType,
    organization,
    currentEnvironment,
    dataSchema,
    graph,
    graphQueryTemplates,
    gridLayouts,
    fieldSchema,
    enabledInquiryTemplates,
    publishedVerificationTemplates,
    onAccountTypeGridLayoutCreate,
    onAccountTypeGridLayoutSave,
  } = props;
  const firstGridLayoutConfiguration =
    gridLayouts[0]?.attributes.configuration ?? generateDefaultAccountTypeGridLayoutConfiguration();

  const [configuration, setConfiguration] = useState(firstGridLayoutConfiguration);
  useEffect(() => {
    if (gridLayouts[0]?.attributes.configuration) {
      setConfiguration(gridLayouts[0]?.attributes.configuration);
    }
  }, [gridLayouts]);

  const [domReady, setDomReady] = useState(false);
  useLayoutEffect(() => {
    setDomReady(true);
  }, []);

  const handleSaveAccountLayout = () => {
    const gridLayout = gridLayouts[0];
    if (!gridLayout) {
      onAccountTypeGridLayoutCreate(configuration);
    } else {
      onAccountTypeGridLayoutSave({
        ...gridLayout,
        attributes: {
          ...gridLayout.attributes,
          configuration,
        },
      });
    }
  };

  // we need to wait for the DOM to be ready before we can select the portal element
  const topButtonContainer = domReady && document.getElementById(TOP_BUTTON_CONTAINER_PORTAL_ID);

  return (
    <>
      {topButtonContainer &&
        createPortal(
          <HStack>
            <Button
              id='account-type__settings'
              variant='basic'
              to={`/dashboard/account-types/${accountTypeId}/settings`}
            >
              Settings
            </Button>
            <Button
              id='account-type__layout-save'
              variant='primary'
              size='md'
              onClick={handleSaveAccountLayout}
            >
              Save
            </Button>
          </HStack>,
          topButtonContainer,
        )}
      <LayoutEditor<AccountModule, Tab<AccountModule>, AccountModuleTypes>
        organization={organization}
        currentEnvironment={currentEnvironment}
        layoutConfiguration={configuration}
        dataSchema={dataSchema}
        environmentName={currentEnvironment?.attributes?.name}
        shouldShowVisibilityToggle={true}
        shouldShowVisibilityAdvancedControls={true}
        shouldShowAdvancedOptions={true}
        supportedModuleTypes={accountModuleTypes}
        supportedComponentTypes={{}}
        generateDefaultModuleConfiguration={generateDefaultAccountModuleConfiguration}
        updateLayoutConfiguration={(configuration: Configuration<AccountModule>) => {
          setConfiguration(configuration);
        }}
        data-test='account-types-show__layout-editor'
        renderModule={(module) => (
          <AccountModuleBasePreview
            key={module.token}
            accountType={accountType}
            fieldSchema={fieldSchema}
            module={module}
          />
        )}
        renderTabForm={(selectedTab, hideEditModal, handleEdit, handleValidateTabName) => (
          <FormEditAccountLayoutTab
            tab={selectedTab}
            cancel={hideEditModal}
            submit={handleEdit}
            validateTabName={handleValidateTabName}
          />
        )}
        renderModuleConfiguration={(module, handleUpdateModule) => {
          switch (module.type) {
            case ModuleTypes.DataDisplay:
              return (
                <DataDisplayPanel
                  key={module.token}
                  module={module}
                  dataSchema={dataSchema}
                  updateModule={handleUpdateModule}
                />
              );
            case ModuleTypes.Markdown:
              return (
                <MarkdownPanel
                  key={module.token}
                  module={module}
                  updateModule={handleUpdateModule}
                />
              );
            case ModuleTypes.AccountInfo:
              return (
                <AccountInfoPanel
                  key={module.token}
                  module={module}
                  updateModule={handleUpdateModule}
                />
              );
            case ModuleTypes.Graphv2:
              return (
                <GraphV2Panel
                  accountStatuses={[]}
                  caseQueries={[]}
                  key={module.token}
                  module={module}
                  graph={graph}
                  graphQueryTemplates={graphQueryTemplates}
                  updateModule={handleUpdateModule}
                  fieldSchema={fieldSchema}
                />
              );
            case ModuleTypes.Resource:
              switch (module['field-key']) {
                case 'inquiries':
                  return (
                    <ResourceInquiryV2Panel
                      enabledInquiryTemplates={enabledInquiryTemplates}
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      updateModule={handleUpdateModule}
                    />
                  );
                case 'reports':
                  return (
                    <ResourceReportV2Panel
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      updateModule={handleUpdateModule}
                    />
                  );
                case 'verifications':
                  return (
                    <ResourceVerificationV2Panel
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      publishedVerificationTemplates={publishedVerificationTemplates}
                      updateModule={handleUpdateModule}
                    />
                  );
              }
              break;
            default: {
              return null;
            }
          }
        }}
      />
    </>
  );
};
